import { useState, useEffect } from 'react';
import { BRAND, FONTS } from '../styles/tokens';
import { Reveal, SectionLabel } from './ui/Reveal';
import { sendLead } from '../services/lead';
import { useIsMobile } from '../hooks/useIsMobile';
import { LEAD_ROLES, LEAD_INTERESTS, SPEAKING_INTEREST } from '../data/content';
import { config } from '../config';
import { track } from '../services/analytics';
import type { LeadFormData, LeadPrefill } from '../types';

const GUIDE_ITEMS = ['Pattern recognition checklist', 'Court-ready documentation language', 'Technology-facilitated abuse red flags', 'Survivor-centered communication framework'];

interface LeadCaptureProps { prefill?: LeadPrefill | null }

export function LeadCapture({ prefill }: LeadCaptureProps = {}) {
  const [formStep, setFormStep] = useState(0);
  const [data, setData] = useState<LeadFormData>({ name: '', email: '', org: '', role: '', interest: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [via, setVia] = useState<'endpoint' | 'mailto' | null>(null);
  const [website, setWebsite] = useState(''); // honeypot — humans never see or fill this
  const m = useIsMobile();

  // Merge a CTA prefill in without clobbering anything the visitor already typed.
  useEffect(() => {
    if (!prefill) return;
    setData(d => ({ ...d, ...prefill, message: d.message || prefill.message || '' }));
  }, [prefill]);

  const isSpeaking = data.interest === SPEAKING_INTEREST;

  const set = (k: keyof LeadFormData, v: string) => setData(d => ({ ...d, [k]: v }));
  const canNext0 = data.name.trim() && data.email.trim() && data.email.includes('@');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (formStep === 0 && canNext0) setFormStep(1);
      else if (formStep === 1) setFormStep(2);
      else if (formStep === 2 && !sending) submit();
    }
  };

  const submit = async () => {
    setSending(true); setError(null);
    try {
      const result = await sendLead({ ...data, website });
      if (result.success) { setVia(result.via); setSubmitted(true); track('lead_submit', { interest: data.interest || 'none', via: result.via }); }
      else setError(result.error || 'Something went wrong.');
    } catch { setError('Network error.'); } finally { setSending(false); }
  };

  const inputStyle = {
    fontFamily: FONTS.body, fontSize: m ? 16 : 15, padding: m ? '16px' : '14px 16px',
    borderRadius: m ? 10 : 8, border: `1.5px solid ${BRAND.border}`,
    background: BRAND.white, color: BRAND.text, outline: 'none',
    width: '100%', boxSizing: 'border-box' as const, transition: 'border-color 0.2s',
    WebkitAppearance: 'none' as const,
  };
  const labelStyle = { fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' as const, color: BRAND.textLight, marginBottom: 6, display: 'block' as const };

  if (submitted) {
    return (
      <section id="lead" style={{ background: BRAND.cream, padding: m ? '64px 20px' : '100px 24px' }}>
        <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: BRAND.successBg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 32 }}>✓</div>
          <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 28 : 34, fontWeight: 700, color: BRAND.navy, marginBottom: 14 }}>Thank you, {data.name}.</h2>
          <p style={{ fontFamily: FONTS.body, fontSize: 16, lineHeight: 1.7, color: BRAND.textMid, marginBottom: 28 }}>Your guide is ready below. Tracy will follow up at <strong>{data.email}</strong> within 48 hours.</p>
          {via === 'mailto' && (
            <p style={{ fontFamily: FONTS.body, fontSize: 14, lineHeight: 1.7, color: BRAND.textLight, marginBottom: 24 }}>Your email app should have opened with your details — please hit send so Tracy receives them. If it didn't open, email <a href={`mailto:${config.tracyEmail}`} style={{ color: BRAND.warmDark }}>{config.tracyEmail}</a> directly.</p>
          )}
          <a href={config.guidePdfUrl} download target="_blank" rel="noopener noreferrer" onClick={() => track('guide_download')}
            style={{ display: 'inline-block', background: BRAND.warm, color: BRAND.navy, fontFamily: FONTS.body, fontSize: m ? 16 : 15, fontWeight: 700, padding: m ? '16px 28px' : '14px 32px', borderRadius: m ? 12 : 8, textDecoration: 'none' }}>
            Download the Guide (PDF) ↓
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="lead" style={{ background: BRAND.cream, padding: m ? '64px 16px' : '100px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1.15fr', gap: m ? 32 : 56, alignItems: 'start' }}>
        <Reveal>
          <SectionLabel>Free Resource</SectionLabel>
          <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 26 : 36, fontWeight: 700, color: BRAND.navy, lineHeight: 1.18, marginBottom: 16 }}>
            Get the Professional's Guide to Recognizing Coercive Control.
          </h2>
          <p style={{ fontFamily: FONTS.body, fontSize: m ? 14 : 15, lineHeight: 1.8, color: BRAND.textMid, marginBottom: 24 }}>
            A practical reference for every professional working with domestic abuse survivors.
          </p>
          {GUIDE_ITEMS.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: `${BRAND.warm}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: BRAND.warm }} />
              </div>
              <span style={{ fontFamily: FONTS.body, fontSize: 14, color: BRAND.text }}>{item}</span>
            </div>
          ))}
        </Reveal>

        <Reveal delay={m ? 0 : 0.12}>
          <div onKeyDown={handleKeyDown} style={{ background: BRAND.white, borderRadius: 16, padding: m ? 24 : 40, boxShadow: '0 4px 24px rgba(0,0,0,0.05)', border: `1px solid ${BRAND.border}` }}>
            {/* Steps */}
            <div style={{ display: 'flex', gap: 6, marginBottom: m ? 20 : 28 }}>
              {['Info', 'Interests', 'Message'].map((s, i) => (
                <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ height: 4, borderRadius: 2, background: formStep >= i ? BRAND.warm : BRAND.borderLight, transition: 'background 0.3s', marginBottom: 6 }} />
                  <span style={{ fontFamily: FONTS.body, fontSize: 10, color: formStep >= i ? BRAND.warm : BRAND.textLight, fontWeight: formStep === i ? 700 : 400, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s}</span>
                </div>
              ))}
            </div>

            {error && (
              <div style={{ background: BRAND.dangerBg, border: `1px solid ${BRAND.danger}30`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontFamily: FONTS.body, fontSize: 13, color: BRAND.danger }}>{error} You can also email Tracy directly at <a href={`mailto:${config.tracyEmail}`} style={{ color: BRAND.danger }}>{config.tracyEmail}</a>.</div>
            )}

            {formStep === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div><label style={labelStyle}>Full Name *</label><input style={inputStyle} placeholder="Your name" value={data.name} onChange={e => set('name', e.target.value)} onFocus={e => e.target.style.borderColor = BRAND.warm} onBlur={e => e.target.style.borderColor = BRAND.border} /></div>
                <div><label style={labelStyle}>Email *</label><input style={inputStyle} type="email" placeholder="you@org.com" value={data.email} onChange={e => set('email', e.target.value)} onFocus={e => e.target.style.borderColor = BRAND.warm} onBlur={e => e.target.style.borderColor = BRAND.border} /></div>
                <div><label style={labelStyle}>Organization</label><input style={inputStyle} placeholder="Your organization" value={data.org} onChange={e => set('org', e.target.value)} onFocus={e => e.target.style.borderColor = BRAND.warm} onBlur={e => e.target.style.borderColor = BRAND.border} /></div>
                <button onClick={() => setFormStep(1)} disabled={!canNext0}
                  style={{ background: canNext0 ? BRAND.warm : BRAND.borderLight, color: canNext0 ? BRAND.navy : BRAND.textLight, border: 'none', padding: m ? '18px' : '14px', borderRadius: m ? 12 : 8, fontSize: m ? 16 : 15, fontWeight: 700, fontFamily: FONTS.body, cursor: canNext0 ? 'pointer' : 'not-allowed', width: '100%' }}>
                  Continue →
                </button>
              </div>
            )}

            {formStep === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <label style={labelStyle}>Your Role</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {LEAD_ROLES.map(r => (
                      <button key={r} onClick={() => set('role', r)} aria-pressed={data.role === r} style={{ fontFamily: FONTS.body, fontSize: m ? 13 : 13, padding: m ? '12px 14px' : '10px 16px', borderRadius: 8, border: `1.5px solid ${data.role === r ? BRAND.warm : BRAND.border}`, background: data.role === r ? BRAND.warmGlow : 'transparent', color: data.role === r ? BRAND.warmDark : BRAND.textMid, cursor: 'pointer', fontWeight: data.role === r ? 600 : 400, transition: 'all 0.2s' }}>{r}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Primary Interest</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {LEAD_INTERESTS.map(r => (
                      <button key={r} onClick={() => set('interest', r)} aria-pressed={data.interest === r} style={{ fontFamily: FONTS.body, fontSize: m ? 13 : 13, padding: m ? '12px 14px' : '10px 16px', borderRadius: 8, border: `1.5px solid ${data.interest === r ? BRAND.warm : BRAND.border}`, background: data.interest === r ? BRAND.warmGlow : 'transparent', color: data.interest === r ? BRAND.warmDark : BRAND.textMid, cursor: 'pointer', fontWeight: data.interest === r ? 600 : 400, transition: 'all 0.2s' }}>{r}</button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={() => setFormStep(0)} style={{ flex: '0 0 auto', background: 'transparent', color: BRAND.textMid, border: `1.5px solid ${BRAND.border}`, padding: m ? '16px 20px' : '14px 24px', borderRadius: m ? 12 : 8, fontSize: 14, fontFamily: FONTS.body, cursor: 'pointer' }}>← Back</button>
                  <button onClick={() => setFormStep(2)} style={{ flex: 1, background: BRAND.warm, color: BRAND.navy, border: 'none', padding: m ? '16px' : '14px', borderRadius: m ? 12 : 8, fontSize: m ? 16 : 15, fontWeight: 700, fontFamily: FONTS.body, cursor: 'pointer' }}>Continue →</button>
                </div>
              </div>
            )}

            {formStep === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} />
                <div><label style={labelStyle}>Message (Optional)</label><textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }} placeholder="Tell Tracy about your needs..." value={data.message} onChange={e => set('message', e.target.value)} onFocus={e => e.target.style.borderColor = BRAND.warm} onBlur={e => e.target.style.borderColor = BRAND.border} /></div>
                <div style={{ background: BRAND.cream, borderRadius: 10, padding: m ? 16 : 20 }}>
                  <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: BRAND.textLight, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Your details</div>
                  <div style={{ fontFamily: FONTS.body, fontSize: m ? 13 : 14, color: BRAND.text, lineHeight: 1.6 }}>
                    <strong>{data.name}</strong> · {data.email}
                    {data.org && <span> · {data.org}</span>}
                    {data.role && <><br />{data.role}</>}
                    {data.interest && <span> · {data.interest}</span>}
                    {data.assessmentId && <><br />Assessment: {data.assessmentId} · {data.assessmentLevel ?? `${data.assessmentScore}%`}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={() => setFormStep(1)} style={{ flex: '0 0 auto', background: 'transparent', color: BRAND.textMid, border: `1.5px solid ${BRAND.border}`, padding: m ? '16px 20px' : '14px 24px', borderRadius: m ? 12 : 8, fontSize: 14, fontFamily: FONTS.body, cursor: 'pointer' }}>←</button>
                  <button onClick={submit} disabled={sending}
                    style={{ flex: 1, background: BRAND.warm, color: BRAND.navy, border: 'none', padding: m ? '18px' : '14px', borderRadius: m ? 12 : 8, fontSize: m ? 16 : 15, fontWeight: 700, fontFamily: FONTS.body, cursor: sending ? 'wait' : 'pointer', opacity: sending ? 0.7 : 1 }}>
                    {sending ? 'Sending to Tracy...' : isSpeaking ? 'Send Booking Inquiry →' : 'Get the Guide →'}
                  </button>
                </div>
                <p style={{ fontFamily: FONTS.body, fontSize: 12, color: BRAND.textLight, textAlign: 'center' }}>Private and never shared. Tracy will receive your details via email.</p>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
